import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Search, Trash2, Pencil, BookText, X, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, notifyErr, inputCls } from '../../../components/admin/billing/shared';
import { useInvoiceAccess, ViewOnlyTag } from '../../../lib/invoiceAccess';

const EMPTY = { code: '', description: '', type: 'SAC', default_gst_rate: 18 };

const HsnMasterPage = () => {
  const { authHeader } = useAuth();
  const { canEdit, viewOnly } = useInvoiceAccess();
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await axios.get(`${API}/gst/hsn${q ? `?q=${encodeURIComponent(q)}` : ''}`, { headers: authHeader() });
      setList(data || []);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, q]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    if (!editing.code || !editing.description) { setErr('Code and description required.'); return; }
    setSaving(true); setErr('');
    try {
      if (editing.id) {
        const { id: _id, created_at: _ca, updated_at: _ua, ...payload } = editing;
        await axios.put(`${API}/gst/hsn/${editing.id}`, payload, { headers: authHeader() });
      } else {
        await axios.post(`${API}/gst/hsn`, editing, { headers: authHeader() });
      }
      setEditing(null);
      await fetchAll();
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  const del = async (h) => {
    if (!window.confirm(`Delete ${h.type} ${h.code}?`)) return;
    try {
      await axios.delete(`${API}/gst/hsn/${h.id}`, { headers: authHeader() });
      await fetchAll();
    } catch (e) { notifyErr(e); }
  };

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-slate-50 min-h-full" data-testid="hsn-master-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BookText className="text-teal-600" size={22} />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">HSN/SAC Master</h1>
          {viewOnly && <ViewOnlyTag />}
        </div>
        {canEdit && (
          <button onClick={() => { setErr(''); setEditing({ ...EMPTY }); }} className="text-sm bg-teal-500 hover:bg-teal-600 text-white font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="new-hsn-btn">
            <Plus size={14} /> Add HSN/SAC
          </button>
        )}
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} pl-8`} placeholder="Search by code or description…" data-testid="hsn-search" />
      </div>

      {err && !editing && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={16} /> Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">No HSN/SAC codes found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold text-slate-600 w-20">Type</th>
                <th className="px-3 py-2 font-semibold text-slate-600 w-32">Code</th>
                <th className="px-3 py-2 font-semibold text-slate-600">Description</th>
                <th className="px-3 py-2 font-semibold text-slate-600 text-right w-24">GST %</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((h) => (
                <tr key={h.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold ${h.type === 'SAC' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}>{h.type}</span></td>
                  <td className="px-3 py-2 font-mono font-bold text-slate-800">{h.code}</td>
                  <td className="px-3 py-2 text-slate-700">{h.description}</td>
                  <td className="px-3 py-2 text-right font-mono">{h.default_gst_rate}%</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      {canEdit && (
                        <button onClick={() => { setErr(''); setEditing({ ...h }); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid={`hsn-edit-${h.code}`}><Pencil size={14} /></button>
                      )}
                      {canEdit && (
                        <button onClick={() => del(h)} className="p-1.5 rounded hover:bg-red-50 text-red-500" data-testid={`hsn-delete-${h.code}`}><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" data-testid="hsn-modal">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h2 className="text-sm font-semibold">{editing.id ? 'Edit' : 'Add'} HSN/SAC</h2>
              <button onClick={() => setEditing(null)} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-sm">{err}</div>}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Type</label>
                  <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className={inputCls} data-testid="hsn-type">
                    <option value="SAC">SAC (Service)</option>
                    <option value="HSN">HSN (Goods)</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-slate-500 mb-1">Code *</label>
                  <input type="text" value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} className={`${inputCls} font-mono`} placeholder="e.g. 998719" data-testid="hsn-code" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Description *</label>
                <textarea rows={2} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className={inputCls} data-testid="hsn-desc" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Default GST Rate (%)</label>
                <select value={editing.default_gst_rate} onChange={(e) => setEditing({ ...editing, default_gst_rate: Number(e.target.value) })} className={inputCls} data-testid="hsn-rate">
                  {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}%</option>)}
                </select>
              </div>
            </div>
            <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="text-sm px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-200">Cancel</button>
              <button onClick={save} disabled={saving} className="text-sm bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold px-3 py-2 rounded-lg" data-testid="hsn-save">{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HsnMasterPage;
